import { useRef, useEffect } from 'react';
import { useFrame } from '@react-three/fiber';

// Performance-optimized useFrame hook that respects GPU tier and frame rate limits
export const useOptimizedFrame = (callback, dependencies = [], maxFPS = 60) => {
  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(0);
  const targetFrameTime = 1000 / maxFPS; // Convert FPS to milliseconds per frame

  useFrame((state, delta) => {
    const now = performance.now();
    const timeSinceLastFrame = now - lastTimeRef.current;

    // Only execute callback if enough time has passed for target FPS
    if (timeSinceLastFrame >= targetFrameTime) {
      callback(state, delta);
      lastTimeRef.current = now;
      frameCountRef.current++;
    }
  }, dependencies);
};

// Hook for conditional frame updates based on visibility and performance
export const useConditionalFrame = (callback, dependencies = [], options = {}) => {
  const {
    maxFPS = 60,
    pauseWhenHidden = true,
    reduceFPSWhenHidden = true,
    hiddenFPS = 10
  } = options;

  const frameCountRef = useRef(0);
  const lastTimeRef = useRef(0);
  const isVisibleRef = useRef(true);
  const targetFrameTime = 1000 / maxFPS;
  const hiddenFrameTime = 1000 / hiddenFPS;

  useEffect(() => {
    const handleVisibilityChange = () => {
      isVisibleRef.current = !document.hidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  useFrame((state, delta) => {
    const now = performance.now();
    const isVisible = isVisibleRef.current;
    
    // Skip if paused when hidden and page is hidden
    if (pauseWhenHidden && !isVisible) {
      return;
    }

    // Determine target frame time based on visibility
    const currentTargetFrameTime = (reduceFPSWhenHidden && !isVisible) 
      ? hiddenFrameTime 
      : targetFrameTime;

    const timeSinceLastFrame = now - lastTimeRef.current;

    // Only execute callback if enough time has passed
    if (timeSinceLastFrame >= currentTargetFrameTime) {
      callback(state, delta);
      lastTimeRef.current = now;
      frameCountRef.current++;
    }
  }, dependencies);
};

// Hook for throttled frame updates (useful for expensive operations)
export const useThrottledFrame = (callback, throttleMs = 16, dependencies = []) => {
  const lastExecutionRef = useRef(0);

  useFrame((state, delta) => {
    const now = performance.now();
    
    if (now - lastExecutionRef.current >= throttleMs) {
      callback(state, delta);
      lastExecutionRef.current = now;
    }
  }, dependencies);
};
